"use client";

import Image from "next/image";
import {
  useEffect,
  useState,
  type ReactNode,
  type SyntheticEvent,
} from "react";
import { Play } from "lucide-react";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { cn } from "~/lib/utils";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./dialog";
import { VideoPlayer } from "./video-player";

const DEFAULT_VIDEO_ASPECT_RATIO = 9 / 16;

interface VideoIntroductionDialogProps {
  videoUrl: string;
  therapistName?: string;
  therapistImage?: string | null;
  avatarClassName?: string;
  playButtonClassName?: string;
  triggerClassName?: string;
  children?: ReactNode;
  autoPlay?: boolean;
  avatarSize?: "small" | "large";
}

export function VideoIntroductionDialog({
  videoUrl,
  therapistName,
  therapistImage,
  avatarClassName,
  playButtonClassName,
  triggerClassName,
  children,
  autoPlay = true,
  avatarSize = "large",
}: VideoIntroductionDialogProps) {
  const [isHydrated, setIsHydrated] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [aspectRatio, setAspectRatio] = useState(DEFAULT_VIDEO_ASPECT_RATIO);
  const [isMetadataReady, setIsMetadataReady] = useState(false);
  const title = `Video predstavljanje${therapistName ? ` - ${therapistName}` : ""}`;

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    setAspectRatio(DEFAULT_VIDEO_ASPECT_RATIO);
    setIsMetadataReady(false);
  };

  const handleMetadataLoaded = (event: SyntheticEvent<HTMLVideoElement>) => {
    const { videoHeight, videoWidth } = event.currentTarget;
    const measuredAspectRatio = videoWidth / videoHeight;

    if (Number.isFinite(measuredAspectRatio) && measuredAspectRatio > 0) {
      setAspectRatio(measuredAspectRatio);
    }

    setIsMetadataReady(true);
  };

  const trigger = children ?? (
    <button
      className={cn(
        "group relative touch-manipulation rounded-full transition-transform duration-200",
        "focus:outline-hidden focus:ring-2 focus:ring-primary focus:ring-offset-2",
        "active:scale-95 md:active:scale-100",
        triggerClassName,
      )}
      aria-label="Pogledaj video predstavljanje"
    >
      <div
        className={cn(
          "relative shrink-0 overflow-hidden rounded-full ring-2 ring-transparent ring-offset-2 transition-shadow duration-200 group-hover:shadow-lg group-hover:ring-primary/20",
          avatarClassName ??
            (avatarSize === "small" ? "h-16 w-16" : "h-40 w-40"),
        )}
      >
        {therapistImage ? (
          <Image
            src={therapistImage}
            alt={therapistName || "Profilna slika terapeuta"}
            width={avatarSize === "small" ? 64 : 320}
            height={avatarSize === "small" ? 64 : 320}
            sizes={avatarSize === "small" ? "64px" : "320px"}
            className="h-full w-full object-cover"
          />
        ) : (
          <span className="flex h-full w-full items-center justify-center bg-linear-to-br from-primary/10 to-primary/5 text-sm">
            {(therapistName || "T")[0]}
          </span>
        )}
      </div>
      {avatarSize === "large" && (
        <span
          className={cn(
            "absolute inset-0 flex items-center justify-center rounded-full bg-black/30 backdrop-blur-[1px]",
            playButtonClassName,
          )}
        >
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background/90 shadow-lg">
            <Play className="ml-1 h-7 w-7 text-primary" />
          </span>
        </span>
      )}
    </button>
  );

  if (!isHydrated) {
    return trigger;
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent
        aria-describedby={undefined}
        className="w-[calc(100%-2rem)] overflow-hidden border-0 bg-black p-0 shadow-2xl"
        style={{
          maxWidth: `min(calc(100vw - 2rem), calc((100vh - 6rem) * ${aspectRatio}))`,
        }}
      >
        <VisuallyHidden>
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
        </VisuallyHidden>
        {isOpen && !isMetadataReady && (
          <>
            <video
              aria-hidden="true"
              className="sr-only"
              onError={() => setIsMetadataReady(true)}
              onLoadedMetadata={handleMetadataLoaded}
              playsInline
              preload="metadata"
              src={videoUrl}
            />
            <div
              className="resimo-video-dialog__loading"
              style={{ aspectRatio: `${aspectRatio}` }}
            >
              {therapistImage && (
                <Image
                  alt=""
                  fill
                  sizes="(max-width: 768px) calc(100vw - 2rem), 640px"
                  src={therapistImage}
                  className="object-cover opacity-70"
                />
              )}
              <div className="resimo-video-dialog__loading-content">
                <span
                  aria-hidden="true"
                  className="resimo-video-dialog__spinner"
                />
                Učitavanje videa…
              </div>
            </div>
          </>
        )}
        {isOpen && isMetadataReady && (
          <VideoPlayer
            src={videoUrl}
            title={title}
            autoPlay={autoPlay}
            aspectRatio={aspectRatio}
            poster={therapistImage ?? undefined}
            posterAlt={
              therapistName ? `Fotografija terapeuta ${therapistName}` : ""
            }
            contextLabel={
              therapistName
                ? `Video predstavljanje, ${therapistName}`
                : "Video predstavljanje"
            }
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
